import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, MapPin, Clock, Plus, Lock, CalendarDays, List, X } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { api, TAG_META } from "@/lib/api";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const TYPES = ["Cultural Festival", "Community", "Arts", "Celebration", "Panel", "Literacy"];

const parseDate = (s) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};

export default function Events() {
  const [events, setEvents] = useState([]);
  const [view, setView] = useState("month");
  const [activeTypes, setActiveTypes] = useState([]);
  const [cursor, setCursor] = useState(new Date(2026, 5, 1));
  const [showAdd, setShowAdd] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = () => api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggleType = (t) =>
    setActiveTypes((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]));

  const filtered = useMemo(
    () => events.filter((e) => activeTypes.length === 0 || activeTypes.includes(e.type)),
    [events, activeTypes]
  );

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstDow = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const eventsByDay = useMemo(() => {
    const map = {};
    filtered.forEach((e) => {
      const d = parseDate(e.date);
      if (d.getFullYear() === year && d.getMonth() === month) {
        (map[d.getDate()] = map[d.getDate()] || []).push(e);
      }
    });
    return map;
  }, [filtered, year, month]);

  const upcoming = useMemo(
    () => [...filtered].sort((a, b) => a.date.localeCompare(b.date)),
    [filtered]
  );

  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div data-testid="events-page">
      <section className="bg-ink crosshatch radial-glow pt-[68px]" data-testid="events-hero">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-16 lg:py-20 relative z-10 flex flex-wrap items-end justify-between gap-6">
          <div>
            <span className="eyebrow">Events Calendar</span>
            <h1 className="font-serif-eth font-light text-white mt-6 text-5xl lg:text-[64px] leading-[1.03]">
              What's <em>happening.</em>
            </h1>
            <p className="font-body-eth text-white/60 text-lg mt-5 max-w-lg">
              Block parties, panels, galas, and book drives — find your next moment with Ethos.
            </p>
          </div>
          <button onClick={() => setShowAdd(true)} data-testid="open-add-event" className="btn-gold font-body-eth text-sm font-semibold px-6 py-3.5 inline-flex items-center gap-2">
            <Plus size={16} /> Add an Event
          </button>
        </div>
      </section>

      <section className="bg-cream">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-14 lg:py-20 grid lg:grid-cols-[240px_1fr] gap-10">
          {/* Sidebar */}
          <aside data-testid="events-sidebar">
            <div className="font-body-eth text-[11px] uppercase tracking-[0.2em] text-mute mb-4">Filter by type</div>
            <div className="flex flex-col gap-2">
              {TYPES.map((t) => (
                <button
                  key={t}
                  onClick={() => toggleType(t)}
                  data-testid={`type-filter-${t.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`flex items-center gap-3 font-body-eth text-sm px-3 py-2.5 text-left transition-colors ${activeTypes.includes(t) ? "bg-teal text-white" : "bg-white border border-[var(--line)] text-ink hover:bg-black/[0.03]"}`}
                  style={{ borderRadius: 2 }}
                >
                  <span className="w-2.5 h-2.5" style={{ background: TAG_META[t], borderRadius: 2 }} />
                  {t}
                </button>
              ))}
            </div>
            {activeTypes.length > 0 && (
              <button onClick={() => setActiveTypes([])} className="mt-4 font-body-eth text-xs text-teal hover:underline">Clear filters</button>
            )}
          </aside>

          {/* Main */}
          <div>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              {view === "month" ? (
                <div className="flex items-center gap-4">
                  <button onClick={() => setCursor(new Date(year, month - 1, 1))} data-testid="cal-prev" className="border border-[var(--line)] w-9 h-9 flex items-center justify-center hover:bg-black/[0.03]" style={{ borderRadius: 2 }}><ChevronLeft size={18} /></button>
                  <div className="font-serif-eth text-3xl text-ink w-[220px] text-center">{MONTHS[month]} {year}</div>
                  <button onClick={() => setCursor(new Date(year, month + 1, 1))} data-testid="cal-next" className="border border-[var(--line)] w-9 h-9 flex items-center justify-center hover:bg-black/[0.03]" style={{ borderRadius: 2 }}><ChevronRight size={18} /></button>
                </div>
              ) : (
                <div className="font-serif-eth text-3xl text-ink">Upcoming</div>
              )}

              <div className="flex border border-[var(--line)] bg-white" style={{ borderRadius: 2 }}>
                <button onClick={() => setView("month")} data-testid="view-month" className={`flex items-center gap-2 font-body-eth text-sm px-4 py-2.5 ${view === "month" ? "bg-teal text-white" : "text-ink"}`}><CalendarDays size={15} /> Month</button>
                <button onClick={() => setView("list")} data-testid="view-list" className={`flex items-center gap-2 font-body-eth text-sm px-4 py-2.5 ${view === "list" ? "bg-teal text-white" : "text-ink"}`}><List size={15} /> List</button>
              </div>
            </div>

            {view === "month" ? (
              <div className="bg-white border border-[var(--line)]" style={{ borderRadius: 2 }} data-testid="month-view">
                <div className="grid grid-cols-7 border-b border-[var(--line)]">
                  {DOW.map((d) => (
                    <div key={d} className="font-body-eth text-[11px] uppercase tracking-wider text-mute text-center py-3">{d}</div>
                  ))}
                </div>
                <div className="grid grid-cols-7">
                  {cells.map((d, i) => (
                    <div key={i} className="min-h-[104px] border-b border-r border-[var(--line)] p-2" style={{ borderRight: (i + 1) % 7 === 0 ? "none" : undefined }}>
                      {d && (
                        <>
                          <div className="font-body-eth text-xs text-mute mb-1">{d}</div>
                          <div className="flex flex-col gap-1">
                            {(eventsByDay[d] || []).map((e) => (
                              <button key={e.id} onClick={() => setSelected(e)} data-testid={`cal-event-${e.id}`} className="text-left font-body-eth text-[11px] leading-tight text-white px-1.5 py-1 truncate hover:opacity-90" style={{ background: TAG_META[e.type] || "#0d4f6e", borderRadius: 2 }}>
                                {e.title}
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-4" data-testid="list-view">
                {upcoming.length === 0 && <div className="font-body-eth text-mute">No events match your filters.</div>}
                {upcoming.map((e, i) => {
                  const dt = parseDate(e.date);
                  return (
                    <Reveal key={e.id} delay={i * 60}>
                      <button onClick={() => setSelected(e)} className="group w-full text-left eth-card bg-white border border-[var(--line)] p-5 flex gap-5 items-center" data-testid={`list-event-${e.id}`}>
                        <div className="shrink-0 w-16 text-center border-r border-[var(--line)] pr-4">
                          <div className="font-body-eth text-[11px] uppercase tracking-wider text-teal">{MONTHS[dt.getMonth()].slice(0, 3)}</div>
                          <div className="font-serif-eth text-3xl text-ink leading-none">{dt.getDate()}</div>
                          <div className="font-body-eth text-[11px] text-mute">{dt.getFullYear()}</div>
                        </div>
                        <div className="flex-1">
                          <span className="font-body-eth text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 text-white" style={{ background: TAG_META[e.type], borderRadius: 2 }}>{e.type}</span>
                          <h3 className="font-serif-eth text-2xl text-ink mt-2 underline-grow inline-block pb-0.5">{e.title}</h3>
                          <div className="flex flex-wrap gap-4 mt-2 font-body-eth text-sm text-mute">
                            {e.time && <span className="inline-flex items-center gap-1.5"><Clock size={14} /> {e.time}</span>}
                            {e.location && <span className="inline-flex items-center gap-1.5"><MapPin size={14} /> {e.location}</span>}
                          </div>
                        </div>
                      </button>
                    </Reveal>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </section>

      {selected && <EventDetail event={selected} onClose={() => setSelected(null)} />}
      {showAdd && <AddEventModal onClose={() => setShowAdd(false)} onSaved={() => { load(); setShowAdd(false); }} />}
    </div>
  );
}

function EventDetail({ event, onClose }) {
  const dt = parseDate(event.date);
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ background: "rgba(5,10,18,0.9)" }} onClick={onClose} data-testid="event-detail">
      <div className="bg-cream max-w-lg w-full p-8 relative" style={{ borderRadius: 2 }} onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-mute hover:text-ink"><X size={22} /></button>
        <span className="font-body-eth text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 text-white" style={{ background: TAG_META[event.type], borderRadius: 2 }}>{event.type}</span>
        <h3 className="font-serif-eth text-3xl text-ink mt-4">{event.title}</h3>
        <div className="flex flex-wrap gap-5 mt-4 font-body-eth text-sm text-mute">
          <span className="inline-flex items-center gap-1.5"><CalendarDays size={15} /> {MONTHS[dt.getMonth()]} {dt.getDate()}, {dt.getFullYear()}</span>
          {event.time && <span className="inline-flex items-center gap-1.5"><Clock size={15} /> {event.time}</span>}
        </div>
        {event.location && <div className="mt-2 font-body-eth text-sm text-mute inline-flex items-center gap-1.5"><MapPin size={15} /> {event.location}</div>}
        {event.description && <p className="font-body-eth text-ink/80 leading-relaxed mt-5">{event.description}</p>}
      </div>
    </div>
  );
}

function AddEventModal({ onClose, onSaved }) {
  const [unlocked, setUnlocked] = useState(false);
  const [pw, setPw] = useState("");
  const [form, setForm] = useState({ title: "", date: "", time: "", type: "Community", location: "", description: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/events", { ...form, password: unlocked ? "Ethos44!" : pw });
      toast.success("Event added to the calendar");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add event");
    } finally {
      setSaving(false);
    }
  };

  const tryUnlock = async (e) => {
    e.preventDefault();
    if (pw === "Ethos44!") {
      setUnlocked(true);
    } else {
      toast.error("Incorrect password");
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ background: "rgba(5,10,18,0.9)" }} onClick={onClose} data-testid="add-event-modal">
      <div className="bg-cream max-w-lg w-full p-8 relative max-h-[90vh] overflow-auto" style={{ borderRadius: 2 }} onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-mute hover:text-ink"><X size={22} /></button>

        {!unlocked ? (
          <form onSubmit={tryUnlock} data-testid="password-gate">
            <div className="flex items-center gap-2 text-teal"><Lock size={18} /><span className="font-body-eth text-[11px] uppercase tracking-[0.2em]">Organizer Access</span></div>
            <h3 className="font-serif-eth text-3xl text-ink mt-4">Add an <em>event.</em></h3>
            <p className="font-body-eth text-mute text-sm mt-2">Enter the organizer password to submit a new event.</p>
            <input
              type="password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder="Password"
              data-testid="event-password-input"
              className="eth-input w-full mt-6 py-2.5 text-ink text-lg"
              autoFocus
            />
            <button type="submit" data-testid="unlock-btn" className="btn-teal w-full mt-6 font-body-eth text-sm font-semibold py-3">Unlock</button>
          </form>
        ) : (
          <form onSubmit={submit} data-testid="event-form">
            <h3 className="font-serif-eth text-3xl text-ink">New <em>event.</em></h3>
            <div className="grid gap-4 mt-6">
              <input required placeholder="Event title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="event-title" className="eth-input w-full py-2.5 text-ink" />
              <div className="grid grid-cols-2 gap-4">
                <input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="event-date" className="eth-input w-full py-2.5 text-ink" />
                <input placeholder="Time (e.g. 6:00 PM)" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="event-time" className="eth-input w-full py-2.5 text-ink" />
              </div>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="event-type" className="eth-input w-full py-2.5 text-ink bg-transparent">
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <input placeholder="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="event-location" className="eth-input w-full py-2.5 text-ink" />
              <textarea placeholder="Description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="event-description" className="eth-input w-full py-2.5 text-ink resize-none" />
            </div>
            <button type="submit" disabled={saving} data-testid="save-event-btn" className="btn-teal w-full mt-6 font-body-eth text-sm font-semibold py-3 disabled:opacity-60">
              {saving ? "Saving..." : "Add Event"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
